import React from "react";
import { DateTime } from "luxon";
import {
    Box,
    Typography,
    TextField,
    RadioGroup,
    FormControlLabel,
    Radio,
    Checkbox,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    OutlinedInput,
} from "@mui/material";
import { Field } from "./prepUi";

// Shared bits of the homework round/recurrence forms — used by both the create wizard
// (RoundWizard) and HomeworkSection's edit dialog, so the two can't drift.

export const WEAKNESS_SOURCES = [
    { value: "ALL", desc: "Use weak chapters from every practice test the student has taken." },
    { value: "EXAMS", desc: "Use weak chapters from only the specific program test(s) you select below." },
    { value: "DATE_WINDOW", desc: "Use weak chapters from tests taken within a date range (the “to” date defaults to the round's release date)." },
];
export const CADENCES = ["DAILY", "EVERY_2_DAYS", "WEEKLY", "BIWEEKLY"];
export const CADENCE_LABELS = {
    DAILY: "Every day", EVERY_2_DAYS: "Every 2 days", WEEKLY: "Every week", BIWEEKLY: "Every 2 weeks",
};
// Day-iterating cadences take no weekday list (the chips hide).
export const cadenceNeedsDays = (c) => c === "WEEKLY" || c === "BIWEEKLY";
export const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
export const MAX_ROUNDS = 200; // keep equal to the server's MAX_MATERIALIZED_ROUNDS
export const QUESTION_MODES = [
    { value: "ADAPTIVE", label: "Adaptive (weakness engine)" },
    { value: "MANUAL_TARGET", label: "Manual chapters" },
    { value: "FIXED_SET", label: "Fixed question set" },
];

// Plain-language homework kinds for the wizard's first step (same values as QUESTION_MODES —
// the API enum never reaches the admin's eyes).
export const HOMEWORK_KINDS = [
    {
        value: "ADAPTIVE",
        title: "Smart picks (recommended)",
        desc: "Each student automatically gets their weakest chapters, based on their tests and practice.",
    },
    {
        value: "MANUAL_TARGET",
        title: "Chapters I choose",
        desc: "You pick the sub-chapters; every student gets the same chapters with a question goal.",
    },
    {
        value: "FIXED_SET",
        title: "Exact question set",
        desc: "Deals a fixed number of specific questions from chapters you pick — the whole class can get the SAME questions, which enables the teacher's most-missed report.",
    },
];

// "HH:mm" → "9:10 AM" for display; empty/legacy-null shows the 09:10 default.
export const fmtHm = (t) => (t ? DateTime.fromFormat(t, "H:mm").toFormat("h:mm a") : "9:10 AM");

// One-line human summary of a rule, e.g. "daily at 4:00 PM" / "weekly Mon·Wed at 9:00 AM".
export const cadenceSummary = (rec) =>
    rec.cadence === "DAILY"
        ? `daily at ${fmtHm(rec.release_time)}`
        : rec.cadence === "EVERY_2_DAYS"
            ? `every 2 days at ${fmtHm(rec.release_time)}`
            : `${(rec.cadence || "WEEKLY").toLowerCase()} ${(rec.release_days_of_week || []).map((d) => DOW[d]).join("·") || "—"} at ${fmtHm(rec.release_time)}`;

// Weakness-source picker (ALL / EXAMS / DATE_WINDOW). `value` carries source_mode /
// source_exam_ids / source_from_date / source_to_date; onChange(patch) merges into it.
export const WeaknessSourceField = ({ value, onChange, programExams }) => (
    <Field label="Weakness source — which test results pick each student's weak chapters">
        <RadioGroup value={value.source_mode} onChange={(e) => onChange({ source_mode: e.target.value })}>
            {WEAKNESS_SOURCES.map((s) => (
                <FormControlLabel
                    key={s.value}
                    value={s.value}
                    control={<Radio size="small" sx={{ pt: 0.25 }} />}
                    sx={{ alignItems: "flex-start", mb: 0.5 }}
                    label={
                        <Box>
                            <Typography variant="body2">{s.value}</Typography>
                            <Typography variant="caption" color="text.secondary">{s.desc}</Typography>
                        </Box>
                    }
                />
            ))}
        </RadioGroup>

        {value.source_mode === "EXAMS" && (
            <FormControl size="small" fullWidth sx={{ mt: 1 }}>
                <InputLabel>Source exams</InputLabel>
                <Select
                    multiple
                    value={value.source_exam_ids}
                    onChange={(e) => onChange({ source_exam_ids: e.target.value })}
                    input={<OutlinedInput label="Source exams" />}
                    renderValue={(ids) =>
                        (programExams || []).filter((ex) => ids.includes(ex.id)).map((ex) => ex.exam_title).join(", ")
                    }
                >
                    {(programExams || []).map((ex) => (
                        <MenuItem key={ex.id} value={ex.id}>
                            <Checkbox checked={value.source_exam_ids.includes(ex.id)} />
                            {ex.exam_title}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        )}

        {value.source_mode === "DATE_WINDOW" && (
            <Box display="flex" gap={2} sx={{ mt: 1 }}>
                <TextField label="From" type="date" size="small" InputLabelProps={{ shrink: true }}
                    value={value.source_from_date} onChange={(e) => onChange({ source_from_date: e.target.value })} />
                <TextField label="To (default: release)" type="date" size="small" InputLabelProps={{ shrink: true }}
                    value={value.source_to_date} onChange={(e) => onChange({ source_to_date: e.target.value })} />
            </Box>
        )}
    </Field>
);
